'use client';

import {AlertCircle} from 'lucide-react';
import {type ReactNode, useMemo, type ComponentType} from 'react';
import {cn} from '../../utils/cn';

const defaultPriceFormatter = (value: number) => {
  const numeric = Number.isFinite(value) ? value : 0;
  return `${numeric.toLocaleString('ru-RU')} ₽`;
};

export interface CartSummaryBreakdownItem {
  label: string;
  amount: number;
  isNegative?: boolean;
  emphasis?: boolean;
}

type ButtonProps = {
  children?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  [key: string]: any;
};

type CardProps = {
  children?: ReactNode;
  className?: string;
  [key: string]: any;
};

export interface CartSummaryCardProps {
  totalLabel?: string;
  totalAmount: number;
  breakdown?: CartSummaryBreakdownItem[];
  priceFormatter?: (value: number) => string;
  freeShippingAmountLeft?: number;
  freeShippingLabel?: string;
  checkoutLabel?: string;
  isCheckoutDisabled?: boolean;
  onCheckout?: () => void;
  hideCheckout?: boolean;
  footer?: ReactNode;
  className?: string;
  ButtonComponent?: ComponentType<ButtonProps>;
  CardComponent?: ComponentType<CardProps>;
}

const DefaultButton: ComponentType<ButtonProps> = ({children, onClick, disabled, className, ...props}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
};

const DefaultCard: ComponentType<CardProps> = ({children, className, ...props}) => {
  return (
    <div
      className={cn(
        'rounded-lg border bg-card text-card-foreground shadow-sm',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

export const CartSummaryCard = ({
  totalLabel = 'Итого',
  totalAmount,
  breakdown,
  priceFormatter = defaultPriceFormatter,
  freeShippingAmountLeft,
  freeShippingLabel = 'До бесплатной доставки осталось',
  checkoutLabel = 'Перейти к оформлению',
  isCheckoutDisabled = false,
  onCheckout,
  hideCheckout = false,
  footer,
  className,
  ButtonComponent = DefaultButton,
  CardComponent = DefaultCard,
}: CartSummaryCardProps) => {
  const displayBreakdown = useMemo(() => breakdown?.filter(Boolean) ?? [], [breakdown]);
  const showFreeShipping = useMemo(() => {
    if (typeof freeShippingAmountLeft !== 'number') return false;
    return freeShippingAmountLeft > 0;
  }, [freeShippingAmountLeft]);

  return (
    <CardComponent className={className}>
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="text-3xl font-semibold md:text-4xl">{totalLabel}</div>
          <div className="text-3xl font-semibold md:text-4xl">
            {priceFormatter(totalAmount)}
          </div>
        </div>

        {showFreeShipping && (
          <div className="mt-4 flex items-center gap-2 text-gray-500">
            <AlertCircle className="h-5 w-5" />
            <span>
              {freeShippingLabel}{' '}
              <span className="font-medium text-gray-700">
                {priceFormatter(freeShippingAmountLeft as number)}
              </span>
            </span>
          </div>
        )}

        {displayBreakdown.length > 0 && (
          <div className="mt-6 space-y-4">
            {displayBreakdown.map(({label, amount, isNegative, emphasis}) => (
              <div
                key={label}
                className={cn(
                  'flex items-center justify-between text-gray-500',
                  emphasis && 'text-gray-800',
                )}
              >
                <div>{label}</div>
                <div
                  className={cn(
                    'text-gray-700',
                    isNegative && 'text-red-500',
                    emphasis && 'font-semibold',
                  )}
                >
                  {isNegative ? `-${priceFormatter(amount)}` : priceFormatter(amount)}
                </div>
              </div>
            ))}
          </div>
        )}

        {footer}

        {!hideCheckout && (
          <ButtonComponent
            className="mt-6"
            disabled={isCheckoutDisabled}
            onClick={onCheckout}
          >
            {checkoutLabel}
          </ButtonComponent>
        )}
      </div>
    </CardComponent>
  );
};
